import Events from 'root/helpers/events';
import { initializeWizardFormLightboxes } from 'root/main/components/forms/ui/wizardFormLightbox';

export default class FormUI {
    readonly #lightboxEl: HTMLElement;
    readonly #formType: string;
    readonly #backgroundColorIdentifier: string;
    readonly #defaultColor: string;
    readonly #formEl: HTMLFormElement | null;
    #formName: string;
    #formOuterHTML: HTMLElement | null;

    constructor(
        formName: string,
        lightboxEl: HTMLElement,
        formEl: HTMLFormElement | null,
        formOuterHTML: HTMLElement | null,
        formType: string
    ) {
        this.#formName = formName;
        this.#lightboxEl = lightboxEl;
        this.#formType = formType;
        this.#backgroundColorIdentifier = '__background-color--';
        this.#defaultColor = 'dark';
        this.#formOuterHTML = formOuterHTML;
        this.#formEl = formEl;
    }

    #getWrapperFormClass() {
        return `js-${this.#formType}-lightbox-wrapper`;
    }

    #getFormBackgroundColor() {
        const formContainer = this.#formOuterHTML?.firstElementChild;
        if (!formContainer) return this.#defaultColor;
        const formClass = formContainer.getAttribute('class');
        if (formClass && formClass.includes(this.#backgroundColorIdentifier)) {
            const backgroundColorPattern = new RegExp(
                `(${this.#backgroundColorIdentifier})(\\w+)`
            );
            const backgroundColorClass = backgroundColorPattern.exec(formClass);
            if (
                backgroundColorClass &&
                backgroundColorClass.length > 0 &&
                backgroundColorClass[2]
            ) {
                return backgroundColorClass[2];
            }
        }
        return this.#defaultColor;
    }

    #setLightboxBackground() {
        const backgroundClass = `${this.#getWrapperFormClass()}${
            this.#backgroundColorIdentifier
        }`;
        const wrapperClasses = this.#lightboxEl.getAttribute('class') || '';
        const backgroundColorModifier = this.#getFormBackgroundColor();
        if (wrapperClasses.includes(backgroundClass)) {
            const backgroundClassPattern = new RegExp(`(${backgroundClass})(\\w+)`);
            this.#lightboxEl.className = wrapperClasses.replace(
                backgroundClassPattern,
                `$1${backgroundColorModifier}`
            );
        } else {
            this.#lightboxEl.classList.add(
                `${backgroundClass}${backgroundColorModifier}`
            );
        }
    }

    #displayLightboxElsOnSubmit(status: 'hide' | 'show') {
        const states = {
            hide: ['add', 'none'],
            show: ['remove', 'block'],
        };
        if (this.#lightboxEl) {
            this.#lightboxEl.classList[states[status][0] as 'add' | 'remove'](
                'js-lightbox-wrapper-no-padding'
            );

            let closeBtn = this.#lightboxEl.children[0] as HTMLElement | null;
            closeBtn = closeBtn?.classList.contains('js-close-btn')
                ? (this.#lightboxEl.children[0] as HTMLElement)
                : (this.#lightboxEl.querySelector('.js-close-btn') as HTMLElement | null);

            if (closeBtn) {
                closeBtn.style.display = states[status][1];
            }
        }
    }

    #formElementsControlOnFormEvents() {
        Events.addListener('back_to_form', () => {
            this.#displayLightboxElsOnSubmit('show');
        });
        Events.addListener('leadform_submit', () => {
            // if the form name includes 'no-redirect', don't hide the lightbox
            if (this.#formName.includes('no-redirect')) {
                this.#displayLightboxElsOnSubmit('show');
            } else {
                this.#displayLightboxElsOnSubmit('hide');
            }
        });
    }

    #importUIToLightboxForm() {
        if (
            this.#formName.includes('steps') &&
            !this.#formName.match(/-b$/) &&
            !this.#formName.includes('wizard-lightbox')
        ) {
            import(
                /*webpackChunkName: "[request]"*/ 'root/main/components/forms/ui/formUISlider'
            ).then(module => {
                if (this.#formEl) {
                    const uiSteps = new module.default(this.#formEl);
                    return uiSteps.loadFormSpecs();
                }
            });
        } else {
            import(
                /*webpackChunkName: "[request]"*/ 'root/main/components/forms/ui/formUI'
            ).then(module => {
                if (this.#formEl) {
                    const ui = new module.default(this.#formEl);
                    return ui.loadFormSpecs();
                }
            });
        }
        if (this.#formName.includes('wizard-lightbox')) {
            initializeWizardFormLightboxes();
        }
    }

    #controlRolAnimationClasses() {
        if (this.#formEl) {
            const fields = this.#formEl.querySelectorAll(
                '[data-formfield=ls-form-field]'
            );
            if (fields && fields.length > 0) {
                let counter = 0;
                Array.from(fields).forEach(field => {
                    counter++;
                    field.classList.add(`js-field-${counter}`);
                    field.addEventListener('animationend', () => {
                        const regex = /js-field-[\d]/;
                        const animationClassRemoved = field.className.replace(regex, '');
                        field.className = animationClassRemoved;
                    });
                });
            }
        }
    }

    render(): void {
        this.#importUIToLightboxForm();
        this.#getFormBackgroundColor();
        this.#formElementsControlOnFormEvents();
        this.#setLightboxBackground();
        this.#controlRolAnimationClasses();
    }
}
